import { oldFormatNumber } from "src/design-system/core/utils/formators";
import type { FormatterOptions } from "src/utils/formatters/common";
import { capitalizeFirst } from "src/utils/formatters/strings";

export const PERCENT_FACTOR = 100;

/**
 * Format a round percent value
 * @param percent Percent value
 * @param options Formatter options
 * @returns Formatted percent
 */
export function oldFormatRoundPercent(
    percent: number,
    {
        hasPlusSign = false,
        showOfferedForOneHundred = false,
        options,
    }: {
        hasPlusSign?: boolean;
        showOfferedForOneHundred?: boolean;
        options?: FormatterOptions;
    } = {}
): string {
    const oneHundredPercentValue = 100 * PERCENT_FACTOR;
    if (showOfferedForOneHundred && percent === oneHundredPercentValue) {
        return capitalizeFirst(options?.translateCommon("concepts.offered"));
    }
    if (percent === null || percent === undefined) {
        return "—";
    }

    return `${hasPlusSign && percent > 0 ? "+" : ""}${oldFormatNumber(Math.round(percent / PERCENT_FACTOR))} %`;
}

/**
 * Format a round percent value
 * @param percent Percent value
 * @param options Formatter options
 * @returns Formatted percent
 */
export function oldFormatFixedPercent(
    percent: number,
    { hasPlusSign = false, hasSpace = true }: { hasPlusSign?: boolean; hasSpace?: boolean } = {}
): string {
    if (percent === null || percent === undefined) {
        return "—";
    }
    const value = percent / PERCENT_FACTOR;

    if (value > 0 && value < 1) {
        return `<1%`;
    } else {
        return `${hasPlusSign && percent > 0 ? "+" : ""}${oldFormatFixedDecimal(value, { decimalCount: 0 })}${
            hasSpace ? " " : ""
        }%`;
    }
}

/**
 * Format a decimal percent value
 * @param percent Percent value
 * @param options Formatter options
 * @returns Formatted percent
 */
export function oldFormatDecimalPercent(
    percent: number,
    { hasPlusSign = false }: { hasPlusSign?: boolean } = {}
): string {
    if (percent === null || percent === undefined) {
        return "—";
    }

    return `${hasPlusSign && percent > 0 ? "+" : ""}${oldFormatFixedDecimal(percent / PERCENT_FACTOR)} %`;
}

/**
 * Format a decimal into a fixed number
 * @param number Decimal number
 * @param options Options
 * @returns Formatted number
 */
export function oldFormatFixedDecimal(
    number: number,
    {
        decimalCount = 1,
        showHyphenIfZero = false,
    }: { decimalCount?: number; showHyphenIfZero?: boolean } = {}
): string {
    if (number === null || number === undefined || (showHyphenIfZero && number === 0)) {
        return "—";
    }
    return oldFormatNumber(Number(Number(number).toFixed(decimalCount)));
}

/**
 *
 * @param number Number to pad
 * @param size Size of padding
 * @returns Padded string
 */
export function oldPadLeadingZeros(number: number, size: number): string {
    let s = number.toString();

    while (s.length < size) {
        s = `0${s}`;
    }

    return s;
}

export interface FormatNumberOptions extends FormatterOptions {
    noDecimal?: boolean;
    showHyphenIfZero?: boolean;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
}
export function formatNumber(
    amount: number,
    {
        noDecimal = false,
        showHyphenIfZero = false,
        language,
        minimumFractionDigits: minimumFractionDigitsProps,
        maximumFractionDigits: maximumFractionDigitsProps,
    }: FormatNumberOptions
): string {
    if (amount === null || amount === undefined || (showHyphenIfZero && amount === 0)) {
        return "—";
    }

    let nmb = amount;
    if (noDecimal) {
        // Rounding negative numbers close to 0 can lead to -0
        // to avoid this we add 0 to the result which converts -0 to 0
        nmb = Math.round(nmb) + 0;
    }

    let minimumFractionDigits: boolean | number = noDecimal && 0;
    if (minimumFractionDigitsProps !== undefined) {
        minimumFractionDigits = minimumFractionDigitsProps;
    }

    const { format }: Intl.NumberFormat = new Intl.NumberFormat(language, {
        minimumFractionDigits,
        maximumFractionDigits: maximumFractionDigitsProps,
    });

    return format(nmb);
}

export function fixSimplePercentValue(value: number, fixedDigit?: number): number {
    let fixedValue = +(value / PERCENT_FACTOR).toFixed(fixedDigit || 1);
    if (fixedValue < 0.1 && fixedValue > -0.1) {
        fixedValue = 0;
    }

    return fixedValue;
}

export function forcePlusSign(value: number): string {
    if (value >= 0) {
        return `+${value}`;
    }

    return `${value}`;
}
